import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import { Button, Chip, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useNotify } from '../../lib/notify';
import { FOODPANDA_PINK, useOutletState, type FoodpandaOutlet } from './api';

const PAUSES = [15, 30, 60, 120];

function stateLabel(o: FoodpandaOutlet) {
  if (o.state === 'OPEN') return 'Open on Foodpanda';
  if (o.state === 'CLOSED') return 'Closed on Foodpanda';
  const until = o.pausedUntil ? new Date(o.pausedUntil) : null;
  return until
    ? `Paused until ${until.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : 'Paused';
}

/** Open, pause (too busy) or close each outlet's Foodpanda store from the order board. */
export function StoreControls({ outlets }: { outlets: FoodpandaOutlet[] }) {
  const active = outlets.filter((o) => o.isActive);
  if (!active.length) return null;
  return (
    <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
      {active.map((o) => (
        <OutletState key={o.id} outlet={o} showName={active.length > 1} />
      ))}
    </Stack>
  );
}

function OutletState({ outlet, showName }: { outlet: FoodpandaOutlet; showName: boolean }) {
  const notify = useNotify();
  const setState = useOutletState();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const run = (body: { state: 'OPEN' | 'CLOSED' | 'PAUSED'; minutes?: number }, done: string) => {
    setAnchor(null);
    setState
      .mutateAsync({ id: outlet.id, version: outlet.version, reason: 'TOO_BUSY', ...body })
      .then(() => notify.success(done), notify.error);
  };
  const color =
    outlet.state === 'OPEN' ? 'success' : outlet.state === 'PAUSED' ? 'warning' : 'default';
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      {showName && (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {outlet.name}
        </Typography>
      )}
      <Chip label={stateLabel(outlet)} color={color} variant="outlined" size="small" />
      <Button
        size="small"
        startIcon={<StorefrontRounded />}
        disabled={setState.isPending}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={`Store on Foodpanda${showName ? `: ${outlet.name}` : ''}`}
        sx={{ color: FOODPANDA_PINK }}
      >
        Store
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {outlet.state !== 'OPEN' && (
          <MenuItem onClick={() => run({ state: 'OPEN' }, 'Store open on Foodpanda')}>
            Open now
          </MenuItem>
        )}
        {PAUSES.map((m) => (
          <MenuItem
            key={m}
            onClick={() =>
              run(
                { state: 'PAUSED', minutes: m },
                `No new Foodpanda orders for ${String(m)} minutes`,
              )
            }
          >
            Too busy: pause {m < 60 ? `${String(m)} min` : `${String(m / 60)} h`}
          </MenuItem>
        ))}
        {outlet.state !== 'CLOSED' && (
          <MenuItem onClick={() => run({ state: 'CLOSED' }, 'Store closed on Foodpanda')}>
            Close for today
          </MenuItem>
        )}
      </Menu>
    </Stack>
  );
}
